// import { useEffect } from "react";
import { Box, useThemeUI } from "theme-ui";
import flow from "lodash/fp/flow";
import replace from "lodash/fp/replace";
import toUpper from "lodash/fp/toUpper";

import { useTranslation } from "hooks";
import { Input } from "components";
import {
  ButtonOptions,
  ButtonWithImageOptions,
  Checkbox,
  DropDown,
  DropDownWithDetails,
  GoalieGearColorOptions,
  GroupedButtonOptions,
  GroupedIconButtonOptions,
  IconButtonOptions,
  SkateCustomiseBlade,
  SkateHolderLength,
} from "components/Forms";
import { fieldHasNoOptionsToRender, getFilteredOptions, getLabel } from "utils";
import {
  ALLOWED_CHARACTERS_WITH_ACCENTS,
  FORM_COMPONENTS,
  PLAYER_NAME_FIELD_NAMES,
} from "utils/constants";

const getLabelWithOptional = ({
  userChoiceValues,
  isRequired,
  hasOptionalFields,
}) => {
  return flow(
    (label) => getLabel({ label, userChoiceValues }),
    (label) => {
      return hasOptionalFields && isRequired === false ? `${label}*` : label;
    }
  );
};

export default function FieldsSelector({
  customisationTitle,
  component,
  userChoices,
  userChoiceValues,
  name,
  isDisabled,
  isRequired,
  isFieldTouched,
  setTouchedField,
  width,
  defaultValueToSetOnRender,
  toolTip,

  // for <Checkbox />
  label: initialLabel,
  labelIcon,
  checkedValue,
  uncheckedValue,

  // for <Input />
  as,
  type,
  initialValue,
  maxLength,
  placeholder,
  hint,
  displayInfo,

  // For Buttons / DropDowns
  options = [],

  // For ButtonOptions
  allowUnselect,

  // For SetOfGroupedIconButtonOptions
  fields = [],

  // For GroupedIconButtonOptions
  resetButtonOption,
  goBackOnSelect,

  hasOptionalFields,

  onChange,

  focusedField,
  setFocusedField,
  goBackFocusedFields,

  props = {},
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const { t } = useTranslation();

  const label = getLabelWithOptional({
    userChoiceValues,
    isRequired,
    component,
    hasOptionalFields,
  })(initialLabel);

  const filteredOptions = getFilteredOptions({ options, userChoiceValues }).map(
    (option) => ({
      ...option,
      label: [option.label, option.isRecommended && t(" - Recommended*")]
        .filter(Boolean)
        .join(""),
    })
  );

  const removeAccents = (str) => {
    return PLAYER_NAME_FIELD_NAMES.includes(name)
      ? str
          .split("")
          .map((char) =>
            ALLOWED_CHARACTERS_WITH_ACCENTS.includes(char)
              ? char
              : char.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
          )
          .join("")
      : str;
  };

  const inputRegex = new RegExp(
    `[^ a-zA-Z0-9-'#.${ALLOWED_CHARACTERS_WITH_ACCENTS}]`,
    "g"
  );

  if (fieldHasNoOptionsToRender({ component, filteredOptions, fields })) {
    return null;
  }

  switch (component) {
    case FORM_COMPONENTS.INPUT:
      return (
        <Box sx={{ width: ({ isMobile }) => (!isMobile && width) || "100%" }}>
          <Input
            variant="round"
            size={isMobile ? "medium" : "large"}
            as={as}
            value={userChoiceValues[name] || initialValue || ""}
            type={type}
            placeholder={placeholder}
            maxLength={maxLength}
            onChange={flow(onChange, () => setTouchedField(name))}
            valueTransform={flow(
              removeAccents,
              replace(inputRegex, ""),
              toUpper
            )}
            isDisabled={isDisabled}
          />
        </Box>
      );

    case FORM_COMPONENTS.CHECKBOX:
      return (
        <Checkbox
          size={isMobile ? "small" : "medium"}
          value={userChoiceValues[name]}
          checkedValue={checkedValue}
          uncheckedValue={uncheckedValue}
          onChange={flow(onChange, () => setTouchedField(name))}
          isDisabled={isDisabled}
          displayInfo={displayInfo}
          applyDisabledDefaultValue={props.applyDisabledDefaultValue}
        >
          {label}
        </Checkbox>
      );

    case FORM_COMPONENTS.BUTTON_OPTIONS:
      return (
        <ButtonOptions
          options={filteredOptions}
          value={userChoiceValues[name]}
          onChange={flow(onChange, () => setTouchedField(name))}
          defaultValueToSetOnRender={defaultValueToSetOnRender}
          allowUnselect={allowUnselect}
        />
      );

    case FORM_COMPONENTS.ICON_BUTTON_OPTIONS:
      return (
        <IconButtonOptions
          options={filteredOptions}
          value={userChoiceValues[name]}
          onChange={flow(onChange, () => setTouchedField(name))}
          defaultValueToSetOnRender={defaultValueToSetOnRender}
        />
      );

    case FORM_COMPONENTS.BUTTON_WITH_IMAGE_OPTIONS:
      return (
        <ButtonWithImageOptions
          customisationTitle={customisationTitle}
          options={filteredOptions}
          value={userChoiceValues[name]}
          onChange={flow(onChange, () => setTouchedField(name))}
        />
      );

    case FORM_COMPONENTS.DROP_DOWN:
      return (
        <DropDown
          label={label && `${label}:`}
          options={filteredOptions}
          value={userChoiceValues[name]}
          placeholder={placeholder}
          hint={hint}
          onChange={flow(onChange, () => setTouchedField(name))}
        />
      );

    case FORM_COMPONENTS.DROP_DOWN_WITH_DETAILS:
      return (
        <DropDownWithDetails
          options={filteredOptions}
          value={userChoiceValues[name]}
          placeholder={placeholder}
          onChange={flow(onChange, () => setTouchedField(name))}
        />
      );

    case FORM_COMPONENTS.GOALIE_GEAR_COLOR_OPTIONS:
      return (
        <GoalieGearColorOptions
          userChoices={userChoices}
          userChoiceValues={userChoiceValues}
          name={name}
          label={label}
          labelIcon={labelIcon}
          fields={fields}
          value={userChoices[name]}
          isFieldTouched={isFieldTouched}
          setTouchedField={setTouchedField}
          onChange={onChange}
          focusedField={focusedField}
          setFocusedField={setFocusedField}
          goBackFocusedFields={goBackFocusedFields}
          toolTip={toolTip}
        />
      );

    case FORM_COMPONENTS.GROUPED_ICON_BUTTON_OPTIONS:
      return (
        <GroupedIconButtonOptions
          name={name}
          label={label}
          labelIcon={labelIcon}
          options={filteredOptions}
          value={userChoices[name]}
          isDisabled={isDisabled}
          isFieldTouched={isFieldTouched}
          setTouchedField={setTouchedField}
          onChange={onChange}
          setFocusedField={() => setFocusedField(name)}
          goBackFocusedFields={goBackFocusedFields}
          defaultValueToSetOnRender={defaultValueToSetOnRender}
          toolTip={toolTip}
          resetButtonOption={resetButtonOption}
          goBackOnSelect={goBackOnSelect}
        />
      );

    case FORM_COMPONENTS.GROUPED_BUTTON_OPTIONS:
      return (
        <GroupedButtonOptions
          label={label}
          labelIcon={labelIcon}
          options={filteredOptions}
          value={userChoices[name]}
          isFieldTouched={isFieldTouched}
          setTouchedField={setTouchedField}
          onChange={onChange}
          setFocusedField={() => setFocusedField(name)}
          goBackFocusedFields={goBackFocusedFields}
          defaultValueToSetOnRender={defaultValueToSetOnRender}
          toolTip={toolTip}
        />
      );

    case FORM_COMPONENTS.SKATE_CUSTOMISE_BLADE:
      return (
        <SkateCustomiseBlade
          name={name}
          outerTitle={props.outerTitle}
          title={props.title}
          bladeImage={props.bladeImage}
          bladeImageForPersonalisation={props.bladeImageForPersonalisation}
          fields={props.fields}
          customiseFeeNote={props?.customiseFeeNote?.label}
          cancelButtonLabel={props.cancelButtonLabel}
          submitButtonLabel={props.submitButtonLabel}
        />
      );

    case FORM_COMPONENTS.SKATE_HOLDER_LENGTH:
      return (
        <SkateHolderLength
          name={name}
          value={userChoiceValues[name]}
          hint={hint}
          onChange={onChange}
        />
      );

    default:
      console.warn(`component missing: ${component}`);
      return null;
  }
}
